import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { useToast } from '../components/ui/use-toast';
import { getMarketBalance, getIncomingTransfersFromApi } from '../services/walletsApi';
import { SendModal, TransferTx } from '../components/wallets/SendModal';
import { ReceiveModal } from '../components/wallets/ReceiveModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Wallet,
  Search,
  ArrowUpRight,
  ArrowDownLeft,
  RefreshCw,
  DollarSign,
  Coins,
  Loader2,
  Shield,
  Copy,
  ExternalLink,
} from 'lucide-react';

/** Market segment from GET /admin/wallets/balance */
interface MarketSegment {
  id: string;
  name: string;
  balance: number;
  type: 'primary' | 'secondary' | 'rental';
}

/** Admin Balance (from GET /admin/wallets/balance) */
interface AdminBalanceItem {
  id: 'admin-balance';
  name: string;
  balance: number;
}

type ListItem = MarketSegment | AdminBalanceItem;

const isAdminBalance = (item: ListItem): item is AdminBalanceItem => item.id === 'admin-balance';

const ADMIN_TX_STORAGE_KEY = 'vestin-admin-wallet-transactions';
const ADMIN_RECEIVES_STORAGE_KEY = 'vestin-admin-wallet-receives';
const MAX_STORED_TXS = 100;

/** Incoming transfer to admin wallet (for Last Activity, Transactions tab, Audit timeline) */
export interface ReceiveTx {
  txHash: string;
  fromAddress: string;
  amount: number;
  timestamp: number;
}

function loadStoredTransactions(): TransferTx[] {
  try {
    const s = localStorage.getItem(ADMIN_TX_STORAGE_KEY);
    if (!s) return [];
    const parsed = JSON.parse(s) as TransferTx[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function loadStoredReceives(): ReceiveTx[] {
  try {
    const s = localStorage.getItem(ADMIN_RECEIVES_STORAGE_KEY);
    if (!s) return [];
    const parsed = JSON.parse(s) as ReceiveTx[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveReceives(receives: ReceiveTx[]) {
  try {
    localStorage.setItem(ADMIN_RECEIVES_STORAGE_KEY, JSON.stringify(receives));
  } catch {
    // ignore
  }
}

function formatShortAddress(addr: string) {
  if (!addr || addr.length < 12) return addr;
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function getTxExplorerUrl(txHash: string): string {
  const networkId = parseInt(process.env.REACT_APP_NETWORK_ID || '97', 10);
  const base = networkId === 97 ? 'https://testnet.bscscan.com' : 'https://bscscan.com';
  return `${base}/tx/${txHash}`;
}

/** Returns relative time e.g. "just now", "5 min ago", "2 hours ago", "3 days ago" */
function formatTimeAgo(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  if (diffSec < 10) return 'just now';
  if (diffSec < 60) return `${diffSec} sec ago`;
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHour < 24) return `${diffHour} hr ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  return new Date(timestamp).toLocaleDateString();
}

/** Format large amounts with K / M / B / T suffix (e.g. $985.75M) */
const formatCurrencyCompact = (amount: number) => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return '$0';
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(2)}K`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
};

const getMarketTypeStyles = (type: MarketSegment['type']) => {
  switch (type) {
    case 'primary':
      return 'bg-primary/20 text-primary border-primary/30';
    case 'secondary':
      return 'bg-success/20 text-success border-success/30';
    case 'rental':
      return 'bg-warning/20 text-warning border-warning/30';
    default:
      return 'bg-muted text-muted-foreground border-border/50';
  }
};

export default function WalletsPage() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [primaryMarketTotal, setPrimaryMarketTotal] = useState<number | null>(null);
  const [secondaryMarketTotal, setSecondaryMarketTotal] = useState<number | null>(null);
  const [rentalMarketTotal, setRentalMarketTotal] = useState<number | null>(null);
  const [marketAdminBalance, setMarketAdminBalance] = useState<number | null>(null);
  const [adminWalletAddress, setAdminWalletAddress] = useState<string | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceError, setBalanceError] = useState<string | null>(null);

  const marketSegments: MarketSegment[] = [
    {
      id: 'primary',
      name: 'Primary Market',
      balance: primaryMarketTotal ?? 0,
      type: 'primary',
    },
    {
      id: 'secondary',
      name: 'Secondary Market',
      balance: secondaryMarketTotal ?? 0,
      type: 'secondary',
    },
    {
      id: 'rental',
      name: 'Rental Market',
      balance: rentalMarketTotal ?? 0,
      type: 'rental',
    },
  ];

  const [selectedItem, setSelectedItem] = useState<ListItem | null>({
    id: 'admin-balance',
    name: 'Admin Balance',
    balance: 0,
  });
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [transactions, setTransactions] = useState<TransferTx[]>(loadStoredTransactions);
  const [receives, setReceives] = useState<ReceiveTx[]>(loadStoredReceives);
  // Track which receive tx hashes we've already shown a toast for (so we show once per new receive)
  const shownToastForTxHashes = useRef<Set<string>>(new Set());
  const hasCompletedInitialReceivesPoll = useRef(false);

  const fetchBalance = useCallback(async () => {
    setBalanceLoading(true);
    setBalanceError(null);
    try {
      const data = await getMarketBalance();
      setPrimaryMarketTotal(
        data.primaryMarketTotal != null && Number.isFinite(data.primaryMarketTotal)
          ? data.primaryMarketTotal
          : null
      );
      setSecondaryMarketTotal(
        data.secondaryMarketTotal != null && Number.isFinite(data.secondaryMarketTotal)
          ? data.secondaryMarketTotal
          : null
      );
      setRentalMarketTotal(
        data.rentalMarketTotal != null && Number.isFinite(data.rentalMarketTotal)
          ? data.rentalMarketTotal
          : null
      );
      setMarketAdminBalance(
        data.adminBalance != null && Number.isFinite(data.adminBalance) ? data.adminBalance : null
      );
      setAdminWalletAddress(data.adminWalletAddress ?? null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load balance';
      setBalanceError(message);
      setPrimaryMarketTotal(null);
      setSecondaryMarketTotal(null);
      setRentalMarketTotal(null);
      setMarketAdminBalance(null);
      setAdminWalletAddress(null);
      toast({
        title: 'Could not load balance',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setBalanceLoading(false);
    }
  }, [toast]);

  const pollReceives = useCallback(() => {
    getIncomingTransfersFromApi()
      .then((rows) => {
        const existing = loadStoredReceives();
        const byHash = new Map<string, ReceiveTx>();
        existing.forEach((r) => byHash.set(r.txHash, r));
        rows.forEach((r) => byHash.set(r.txHash, { ...r }));
        const merged = Array.from(byHash.values())
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, MAX_STORED_TXS);
        saveReceives(merged);
        window.dispatchEvent(new CustomEvent('vestin-wallet-receives-updated'));
        setReceives((_prev) => {
          const isFirstPoll = !hasCompletedInitialReceivesPoll.current;
          hasCompletedInitialReceivesPoll.current = true;
          // New receives = in this poll but we haven't shown a toast for them yet
          const notYetShown = merged.filter(
            (r) => r.txHash && !shownToastForTxHashes.current.has(r.txHash)
          );
          if (isFirstPoll) {
            // Don't show toast on first poll (e.g. when navigating back to Wallets page) — mark all as seen
            merged.forEach((r) => {
              if (r.txHash) shownToastForTxHashes.current.add(r.txHash);
            });
          } else if (notYetShown.length > 0) {
            // Subsequent poll with new receive(s) — show toast once for the latest
            const first = notYetShown[0];
            const amount = Number(first.amount).toLocaleString(undefined, {
              maximumFractionDigits: 2,
            });
            toast({
              title: 'Transaction successful',
              description: `You received ${amount} tokens. Balance updated.`,
            });
            fetchBalance();
            notYetShown.forEach((r) => shownToastForTxHashes.current.add(r.txHash));
          }
          return merged;
        });
      })
      .catch((err) => {
        console.error('[Wallets] Failed to fetch receives:', err);
        const existing = loadStoredReceives();
        setReceives(existing);
      });
  }, [toast, fetchBalance]);

  const handleTransferSuccess = useCallback(
    (tx: TransferTx) => {
      setTransactions((prev) => {
        const next = [tx, ...prev].slice(0, MAX_STORED_TXS);
        try {
          localStorage.setItem(ADMIN_TX_STORAGE_KEY, JSON.stringify(next));
        } catch {
          // ignore
        }
        return next;
      });
      fetchBalance();
      setTimeout(() => {
        fetchBalance();
        pollReceives();
      }, 4000);
    },
    [fetchBalance, pollReceives]
  );

  useEffect(() => {
    fetchBalance();
    pollReceives();
  }, [fetchBalance, pollReceives]);

  const handleReceiveModalOpenChange = useCallback((open: boolean) => {
    setReceiveModalOpen(open);
  }, []);

  const allActivities = useMemo(() => {
    const sends = transactions.map((t) => ({ ...t, kind: 'send' as const }));
    const recs = receives.map((r) => ({ ...r, kind: 'receive' as const }));
    return [...sends, ...recs].sort((a, b) => b.timestamp - a.timestamp);
  }, [transactions, receives]);

  const [, setTimeTick] = useState(0);
  useEffect(() => {
    if (allActivities.length === 0) return;
    // Refresh "Last Activity" / time-ago every 15s so "just now" and "X min ago" stay correct
    const id = setInterval(() => setTimeTick((t) => t + 1), 15000);
    return () => clearInterval(id);
  }, [allActivities.length]);

  const adminBalance =
    marketAdminBalance != null && Number.isFinite(marketAdminBalance) ? marketAdminBalance : 0;
  const primaryBalance =
    primaryMarketTotal != null && Number.isFinite(primaryMarketTotal) ? primaryMarketTotal : 0;
  const secondaryBalance =
    secondaryMarketTotal != null && Number.isFinite(secondaryMarketTotal)
      ? secondaryMarketTotal
      : 0;
  const rentalBalance =
    rentalMarketTotal != null && Number.isFinite(rentalMarketTotal) ? rentalMarketTotal : 0;

  const adminBalanceItem: AdminBalanceItem = {
    id: 'admin-balance',
    name: 'Admin Balance',
    balance: adminBalance,
  };

  const listItems: ListItem[] = [adminBalanceItem, ...marketSegments];
  const filteredListItems = listItems.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const copyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    toast({ title: 'Address copied to clipboard' });
  };

  // Admin wallet address: from API or fallback from env (so it always shows when configured)
  const displayAdminWalletAddress =
    (adminWalletAddress && adminWalletAddress.trim()) ||
    (typeof process.env.REACT_APP_ADMIN_WALLET_ADDRESS === 'string' &&
      process.env.REACT_APP_ADMIN_WALLET_ADDRESS.trim()) ||
    '';

  return (
    <div className='space-y-6 animate-fade-in'>
      <SendModal
        open={sendModalOpen}
        onOpenChange={setSendModalOpen}
        senderAddress={displayAdminWalletAddress}
        onSuccess={handleTransferSuccess}
      />
      <ReceiveModal
        open={receiveModalOpen}
        onOpenChange={handleReceiveModalOpenChange}
        receiveAddress={displayAdminWalletAddress}
        onCheckReceives={pollReceives}
      />
      {/* Header */}
      <div className='flex flex-col lg:flex-row lg:items-center justify-between gap-4'>
        <div className='flex items-center gap-4'>
          <div className='w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center'>
            <Wallet className='w-6 h-6 text-primary-foreground' />
          </div>
          <div>
            <h1 className='text-2xl font-bold tracking-tight'>Wallet Management</h1>
            <p className='text-muted-foreground'>Monitor wallets, balances, and transactions</p>
          </div>
        </div>
        <div className='flex gap-2'>
          <Button
            variant='outline'
            onClick={() => {
              fetchBalance();
              pollReceives();
            }}
            disabled={balanceLoading}
          >
            {balanceLoading ? (
              <Loader2 className='w-4 h-4 mr-2 animate-spin' />
            ) : (
              <RefreshCw className='w-4 h-4 mr-2' />
            )}
            Sync
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-4'>
        <Card className='gradient-card border-border/30'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center'>
                {balanceLoading ? (
                  <Loader2 className='w-5 h-5 text-amber-600 animate-spin' />
                ) : (
                  <Shield className='w-5 h-5 text-amber-600' />
                )}
              </div>
              <div>
                <p className='text-xl font-bold'>
                  {balanceLoading
                    ? '—'
                    : balanceError
                      ? formatCurrencyCompact(0)
                      : formatCurrencyCompact(adminBalance)}
                </p>
                <p className='text-xs text-muted-foreground'>Admin Balance (wallet)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className='gradient-card border-border/30'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center'>
                {balanceLoading ? (
                  <Loader2 className='w-5 h-5 text-primary animate-spin' />
                ) : (
                  <DollarSign className='w-5 h-5 text-primary' />
                )}
              </div>
              <div>
                <p className='text-xl font-bold'>
                  {balanceLoading
                    ? '—'
                    : balanceError
                      ? formatCurrencyCompact(0)
                      : formatCurrencyCompact(primaryBalance)}
                </p>
                <p className='text-xs text-muted-foreground'>Primary Market</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className='gradient-card border-border/30'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center'>
                {balanceLoading ? (
                  <Loader2 className='w-5 h-5 text-success animate-spin' />
                ) : (
                  <Coins className='w-5 h-5 text-success' />
                )}
              </div>
              <div>
                <p className='text-xl font-bold'>
                  {balanceLoading
                    ? '—'
                    : balanceError
                      ? formatCurrencyCompact(0)
                      : formatCurrencyCompact(secondaryBalance)}
                </p>
                <p className='text-xs text-muted-foreground'>Secondary Market</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className='gradient-card border-border/30'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 rounded-lg bg-info/20 flex items-center justify-center'>
                {balanceLoading ? (
                  <Loader2 className='w-5 h-5 text-info animate-spin' />
                ) : (
                  <Coins className='w-5 h-5 text-info' />
                )}
              </div>
              <div>
                <p className='text-xl font-bold'>
                  {balanceLoading
                    ? '—'
                    : balanceError
                      ? formatCurrencyCompact(0)
                      : formatCurrencyCompact(rentalBalance)}
                </p>
                <p className='text-xs text-muted-foreground'>Rental Market</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className='grid lg:grid-cols-3 gap-6'>
        {/* Wallets & Markets List */}
        <Card className='gradient-card border-border/30'>
          <CardHeader className='pb-3'>
            <CardTitle className='text-lg'>Wallets & Markets</CardTitle>
            <div className='relative mt-2'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground' />
              <Input
                placeholder='Search...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='pl-10 bg-muted/50 border-border/50'
              />
            </div>
          </CardHeader>
          <CardContent className='p-0'>
            <ScrollArea className='h-[400px]'>
              <div className='divide-y divide-border/50'>
                {filteredListItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => setSelectedItem(item)}
                    className={`p-4 cursor-pointer transition-colors hover:bg-muted/30 ${
                      selectedItem?.id === item.id ? 'bg-muted/50' : ''
                    }`}
                  >
                    <div className='flex items-center justify-between mb-2'>
                      <h4 className='font-medium'>{item.name}</h4>
                      <Badge
                        variant='outline'
                        className={`text-xs capitalize ${
                          isAdminBalance(item)
                            ? 'bg-amber-500/20 text-amber-600 border-amber-500/30'
                            : getMarketTypeStyles(item.type)
                        }`}
                      >
                        {isAdminBalance(item) ? 'admin' : item.type}
                      </Badge>
                    </div>
                    <div className='flex items-center justify-end text-sm'>
                      <span className='font-semibold'>{formatCurrencyCompact(item.balance)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Detail panel: Admin Balance (Main Treasury-style UI) or Market */}
        <Card className='lg:col-span-2 gradient-card border-border/30'>
          {selectedItem && isAdminBalance(selectedItem) ? (
            <Tabs defaultValue='details'>
              <CardHeader className='pb-0'>
                <div className='flex items-center justify-between'>
                  <CardTitle className='text-lg'>Admin Balance</CardTitle>
                  <TabsList className='bg-muted/50'>
                    <TabsTrigger value='details'>Details</TabsTrigger>
                    <TabsTrigger value='transactions'>Transactions</TabsTrigger>
                  </TabsList>
                </div>
              </CardHeader>
              <CardContent>
                <TabsContent value='details' className='mt-4'>
                  <div className='space-y-6'>
                    <div className='p-4 rounded-xl bg-muted/30 border border-border/50'>
                      <div className='flex items-center justify-between mb-4'>
                        <span className='text-sm text-muted-foreground'>Admin Wallet Address</span>
                        {displayAdminWalletAddress && (
                          <div className='flex gap-2'>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => copyAddress(displayAdminWalletAddress)}
                            >
                              <Copy className='w-4 h-4' />
                            </Button>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() =>
                                window.open(
                                  `https://bscscan.com/address/${displayAdminWalletAddress}`,
                                  '_blank'
                                )
                              }
                            >
                              <ExternalLink className='w-4 h-4' />
                            </Button>
                          </div>
                        )}
                      </div>
                      <p className='font-mono text-sm break-all'>
                        {displayAdminWalletAddress || '—'}
                      </p>
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                      <div className='p-4 rounded-xl bg-muted/30 border border-border/50'>
                        <p className='text-sm text-muted-foreground mb-1'>Balance</p>
                        <p className='text-2xl font-bold'>
                          {balanceLoading ? '—' : formatCurrencyCompact(adminBalance)}
                        </p>
                        <p className='text-xs text-muted-foreground mt-1'>USD</p>
                      </div>
                      <div className='p-4 rounded-xl bg-muted/30 border border-border/50'>
                        <p className='text-sm text-muted-foreground mb-1'>Last Activity</p>
                        <p className='text-lg font-semibold'>
                          {allActivities.length > 0
                            ? formatTimeAgo(allActivities[0].timestamp)
                            : '—'}
                        </p>
                      </div>
                    </div>

                    <div className='flex gap-2'>
                      <Button
                        type='button'
                        className='flex-1'
                        onClick={() => setSendModalOpen(true)}
                        disabled={!displayAdminWalletAddress}
                      >
                        <ArrowUpRight className='w-4 h-4 mr-2' />
                        Send
                      </Button>
                      <Button
                        type='button'
                        variant='outline'
                        className='flex-1'
                        onClick={() => setReceiveModalOpen(true)}
                        disabled={!displayAdminWalletAddress}
                      >
                        <ArrowDownLeft className='w-4 h-4 mr-2' />
                        Receive
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value='transactions' className='mt-4'>
                  {allActivities.length === 0 ? (
                    <div className='p-8 text-center text-muted-foreground rounded-xl bg-muted/30 border border-border/50'>
                      No transactions
                    </div>
                  ) : (
                    <div className='space-y-2'>
                      <div className='flex items-center gap-4 px-3 py-2 text-sm font-medium text-muted-foreground border-b border-border/50 rounded-t-lg bg-muted/30'>
                        <span className='w-20'>Type</span>
                        <span className='flex-1 min-w-0'>From / To</span>
                        <span className='w-24 text-right'>Amount</span>
                        <span className='w-32'>Activity</span>
                        <span className='w-10' />
                      </div>
                      {allActivities.map((activity) =>
                        activity.kind === 'send' ? (
                          <div
                            key={`send-${activity.txHash}`}
                            className='flex items-center gap-4 px-3 py-3 text-sm rounded-lg bg-muted/20 border border-border/50 hover:bg-muted/30'
                          >
                            <span className='w-20'>
                              <Badge variant='secondary' className='font-normal'>
                                Send
                              </Badge>
                            </span>
                            <span
                              className='flex-1 min-w-0 font-mono truncate'
                              title={activity.toAddress}
                            >
                              {formatShortAddress(activity.toAddress)}
                            </span>
                            <span className='w-24 text-right font-medium'>
                              {Number(activity.amount).toLocaleString(undefined, {
                                maximumFractionDigits: 6,
                              })}
                            </span>
                            <span
                              className='w-32 text-muted-foreground'
                              title={new Date(activity.timestamp).toLocaleString()}
                            >
                              {formatTimeAgo(activity.timestamp)}
                            </span>
                            <a
                              href={getTxExplorerUrl(activity.txHash)}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-primary hover:underline'
                              title='View on explorer'
                            >
                              <ExternalLink className='w-4 h-4' />
                            </a>
                          </div>
                        ) : (
                          <div
                            key={`receive-${activity.txHash}`}
                            className='flex items-center gap-4 px-3 py-3 text-sm rounded-lg bg-muted/20 border border-border/50 hover:bg-muted/30'
                          >
                            <span className='w-20'>
                              <Badge variant='outline' className='font-normal'>
                                Receive
                              </Badge>
                            </span>
                            <span
                              className='flex-1 min-w-0 font-mono truncate'
                              title={activity.fromAddress}
                            >
                              {formatShortAddress(activity.fromAddress)}
                            </span>
                            <span className='w-24 text-right font-medium'>
                              {Number(activity.amount).toLocaleString(undefined, {
                                maximumFractionDigits: 6,
                              })}
                            </span>
                            <span
                              className='w-32 text-muted-foreground'
                              title={new Date(activity.timestamp).toLocaleString()}
                            >
                              {formatTimeAgo(activity.timestamp)}
                            </span>
                            <a
                              href={getTxExplorerUrl(activity.txHash)}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-primary hover:underline'
                              title='View on explorer'
                            >
                              <ExternalLink className='w-4 h-4' />
                            </a>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </TabsContent>
              </CardContent>
            </Tabs>
          ) : selectedItem && !isAdminBalance(selectedItem) ? (
            <>
              <CardHeader>
                <CardTitle className='text-lg'>{selectedItem.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-6 mt-4'>
                  <div className='p-4 rounded-xl bg-muted/30 border border-border/50'>
                    <p className='text-sm text-muted-foreground mb-1'>
                      Total balance in this market
                    </p>
                    <p className='text-2xl font-bold'>
                      {formatCurrencyCompact(
                        marketSegments.find((s) => s.id === selectedItem.id)?.balance ??
                          selectedItem.balance
                      )}
                    </p>
                    <p className='text-xs text-muted-foreground mt-1'>
                      From GET /admin/wallets/balance
                    </p>
                  </div>
                  <div className='p-4 rounded-xl bg-muted/30 border border-border/50'>
                    <p className='text-sm text-muted-foreground'>
                      {selectedItem.type === 'primary' &&
                        'Primary market: total invested amount (InvestPool, positive only).'}
                      {selectedItem.type === 'secondary' &&
                        'Secondary market: resell pool total value.'}
                      {selectedItem.type === 'rental' && 'Rental market: total rental value.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle className='text-lg'>Select a Wallet or Market</CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-muted-foreground text-sm mt-4'>
                  Select an item from the list to view details.
                </p>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
